import { z } from 'zod';
import { api } from './api';
import { roleSchema } from '@/types/schemas';
import { telegramLinkSchema, type TelegramLink } from './telegram.service';

/** A unit inside somebody's access scope. */
export const businessUnitScopeSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
});

/** A person's Telegram, as the owner sees it. Never a token or a deep link. */
export const telegramUserStateSchema = z.object({
  connected: z.boolean(),
  username: z.string().nullable().optional().default(null),
  connected_at: z.string().nullable().optional().default(null),
  link_pending: z.boolean().default(false),
  link_expires_at: z.string().nullable().optional().default(null),
});

export const userSchema = z.object({
  id: z.string(),
  email: z.string(),
  full_name: z.string(),
  status: z.string(),
  is_protected: z.boolean(),
  last_login_at: z.string().nullable().optional(),
  role: roleSchema,
  // «Вся компания» — every unit, including ones created later.
  scope_all_units: z.boolean().optional().default(true),
  scope_business_units: z.array(businessUnitScopeSchema).default([]),
  // Provisioned by the owner only.
  telegram: telegramUserStateSchema.optional(),
});

export const assignableRoleSchema = z.object({ code: z.string(), name: z.string() });

/** PENDING | ACCEPTED | REVOKED | EXPIRED */
export const INVITATION_STATES = ['PENDING', 'ACCEPTED', 'REVOKED', 'EXPIRED'] as const;

export const invitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  full_name: z.string(),
  role: roleSchema,
  state: z.enum(INVITATION_STATES),
  scope_all_units: z.boolean(),
  scope_business_units: z.array(businessUnitScopeSchema).default([]),
  expires_at: z.string(),
  created_at: z.string(),
  accepted_at: z.string().nullable().optional(),
  revoked_at: z.string().nullable().optional(),
  invited_by_id: z.string(),
});

/** The link is returned once, at creation or renewal, and never again — only
 * the token's hash is stored server-side. */
export const invitationIssuedSchema = z.object({
  invitation: invitationSchema,
  invite_link: z.string(),
});

export const invitationPreviewSchema = z.object({
  company_name: z.string(),
  email: z.string(),
  full_name: z.string(),
  role: roleSchema,
  expires_at: z.string(),
});

export type BusinessUnitScope = z.infer<typeof businessUnitScopeSchema>;
export type TelegramUserState = z.infer<typeof telegramUserStateSchema>;
export type User = z.infer<typeof userSchema>;
export type AssignableRole = z.infer<typeof assignableRoleSchema>;
export type Invitation = z.infer<typeof invitationSchema>;
export type InvitationIssued = z.infer<typeof invitationIssuedSchema>;
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

export interface InvitationInput {
  email: string;
  full_name: string;
  role: string;
  scope_all_units: boolean;
  business_unit_ids: string[];
}

export interface ScopeInput {
  scope_all_units: boolean;
  business_unit_ids: string[];
}

export const usersService = {
  list: (signal?: AbortSignal): Promise<User[]> =>
    api.get('/users', z.array(userSchema), signal),

  assignableRoles: (signal?: AbortSignal): Promise<AssignableRole[]> =>
    api.get('/users/assignable-roles', z.array(assignableRoleSchema), signal),

  /** Owner-only; the server re-checks and audits the change. */
  changeRole: (userId: string, role: string): Promise<User> =>
    api.patch(`/users/${userId}/role`, userSchema, { role }),

  changeScope: (userId: string, scope: ScopeInput): Promise<User> =>
    api.patch(`/users/${userId}/scope`, userSchema, scope),

  disable: (userId: string): Promise<User> =>
    api.post(`/users/${userId}/disable`, userSchema),

  enable: (userId: string): Promise<User> => api.post(`/users/${userId}/enable`, userSchema),

  // --- Telegram provisioning: owner-only, always for a named user ---
  /** Mints the one-time deep link. Shown once — the server stores only its hash. */
  telegramLink: (userId: string): Promise<TelegramLink> =>
    api.post(`/users/${userId}/telegram/link`, telegramLinkSchema),

  /** Kills a link that was issued but never used. */
  telegramRevokeLink: (userId: string): Promise<User> =>
    api.post(`/users/${userId}/telegram/link/revoke`, userSchema),

  telegramDisconnect: (userId: string): Promise<User> =>
    api.post(`/users/${userId}/telegram/disconnect`, userSchema),
};

export const invitationsService = {
  list: (signal?: AbortSignal): Promise<Invitation[]> =>
    api.get('/invitations', z.array(invitationSchema), signal),

  create: (input: InvitationInput): Promise<InvitationIssued> =>
    api.post('/invitations', invitationIssuedSchema, input),

  revoke: (id: string): Promise<Invitation> =>
    api.post(`/invitations/${id}/revoke`, invitationSchema),

  /** Reissues the token — the previous link stops working immediately. */
  renew: (id: string): Promise<InvitationIssued> =>
    api.post(`/invitations/${id}/renew`, invitationIssuedSchema),

  // --- public: the invitee has no account yet ---
  preview: (token: string, signal?: AbortSignal): Promise<InvitationPreview> =>
    api.get(`/invitations/preview/${encodeURIComponent(token)}`, invitationPreviewSchema, signal),

  accept: (token: string, password: string, passwordConfirm: string): Promise<void> =>
    api.post('/invitations/accept', z.void(), {
      token,
      password,
      password_confirm: passwordConfirm,
    }),
};
